import type { Filter } from "../filters/model";
import { STORE_TIMEZONE } from "../filters/timezone";
import type { IsoDate, IsoDateTime, Money } from "../primitives";

/**
 * Orders over time (#449): the series a line chart draws, and the arithmetic
 * both data sources share to produce it.
 *
 * The API counts in Postgres and the fixture adapter counts in memory, and the
 * two have to agree to the last bucket — a chart that differs between a
 * rehearsal on fixtures and the real thing is a chart nobody trusts. So what
 * can be shared is shared: which calendar day an instant falls on in the
 * store's zone, where a week or a month starts, the zero-filled bucket list,
 * the top-N-and-Other fold, and decimal-exact money sums all live here. The
 * only thing each side supplies on its own is the grouped rows — Postgres by
 * `group by`, the fixture by walking its orders.
 */

export const ORDER_SERIES_BUCKETS = ["day", "week", "month"] as const;
export type OrderSeriesBucket = (typeof ORDER_SERIES_BUCKETS)[number];

/** How many orders, or what they add up to. */
export const ORDER_SERIES_METRICS = ["orders", "value"] as const;
export type OrderSeriesMetric = (typeof ORDER_SERIES_METRICS)[number];

/**
 * The order fields a chart can draw one line per value of.
 *
 * An allowlist, and the same four on both sides: the API maps each to the SQL
 * expression its order filter already uses for that field, so "split by
 * payment" and "filter by payment" cannot mean different things. Anything else
 * — a customer, an order number — has a value per order, which is a chart of
 * one-point lines.
 */
export const ORDER_SERIES_SPLITS = [
  "payment",
  "deliveryStatus",
  "sourceApp",
  "shippingProvinceCode",
] as const;
export type OrderSeriesSplit = (typeof ORDER_SERIES_SPLITS)[number];

/**
 * Lines drawn before the rest are folded into one "Other".
 *
 * Shipping state has thirty-odd values in a year and a line chart past about
 * eight reads as a tangle rather than a comparison. The largest by the chosen
 * metric keep their own line.
 */
export const MAX_SERIES_LINES = 8;

/**
 * The longest range one chart will draw: ten years of days.
 *
 * Not a product decision so much as a bound — the bucket list is built in
 * memory and a typed-in `from=0001-01-01` should be refused, not expanded.
 */
export const MAX_SERIES_DAYS = 3660;

/**
 * The years a chart's dates may name, inclusive.
 *
 * The store opened in 2026; a date outside 2000..2100 is a typo or a hostile
 * link, never a question about orders. The cap is not only tidiness: the last
 * dates the calendar can write (`9999-12-31`) are real dates a two-day range
 * can name, and stepping a bucket past them leaves the four-digit years every
 * date comparison here assumes. A range is refused before it gets that far,
 * and `bucketStarts` cannot loop for ever whatever reaches it.
 */
export const MIN_SERIES_YEAR = 2000;
export const MAX_SERIES_YEAR = 2100;

/** What a chart asks for. `from` and `to` are the store's calendar dates. */
export interface OrderSeriesQuery {
  /** The same filter tree the orders list takes — every order field works. */
  filter?: Filter;
  /** The same free-text search the orders list takes. */
  search?: string;
  /** First day counted, inclusive, in the store's timezone. */
  from: IsoDate;
  /** Last day counted, inclusive, in the store's timezone. */
  to: IsoDate;
  bucket: OrderSeriesBucket;
  metric: OrderSeriesMetric;
  /** One line per value of this field. Absent draws a single line. */
  splitBy?: OrderSeriesSplit;
  /** Cancelled orders are left out unless this is true. */
  includeCancelled?: boolean;
}

/** One line on the chart. */
export interface OrderSeriesLine {
  /**
   * The split field's value — `cod`, `delivered`, `MH`. Null for the single
   * line of an unsplit chart, and for orders that have no value for the field.
   */
  key: string | null;
  /** True for the line that folds every value past `MAX_SERIES_LINES`. */
  other: boolean;
  /**
   * One figure per entry in `buckets`, as decimal strings: whole numbers for
   * the `orders` metric, money for `value`. Strings because money is never a
   * float (`Money`), and one shape for both metrics is simpler to hold than
   * two.
   */
  values: string[];
  /** The line's figures added up, in the same form as `values`. */
  total: string;
}

/**
 * The chart's data. No row of an order is in it — only counts and sums, so a
 * browser draws a year of orders from a few kilobytes.
 */
export interface OrderSeries {
  from: IsoDate;
  to: IsoDate;
  bucket: OrderSeriesBucket;
  metric: OrderSeriesMetric;
  splitBy: OrderSeriesSplit | null;
  /** The zone the days were cut in. */
  timezone: string;
  /**
   * The first day of every bucket in the range, in order, zero-filled — a day
   * with no orders is a point at zero, not a gap the line skips across. A week
   * starts on Monday and a month on the 1st, so the first and last bucket may
   * begin before `from` or run past `to`; only orders inside the range count.
   */
  buckets: IsoDate[];
  lines: OrderSeriesLine[];
  /** Everything in range that matched, whichever metric is drawn. */
  totals: { orders: number; value: Money };
}

/** What one group of the grouped query holds. */
export interface OrderSeriesRow {
  /** The bucket's first day. */
  bucket: IsoDate;
  /** The split field's value; null with no split, or with no value. */
  key: string | null;
  orders: number;
  value: Money;
}

/* ---------------------------------------------------------------- calendar */

const DAY_MS = 86_400_000;

/** `2026-03-10` as a UTC instant — date arithmetic with no zone in play. */
function utc(date: IsoDate): number {
  const [year, month, day] = date.split("-").map(Number);
  return Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

/**
 * A UTC instant as `YYYY-MM-DD`.
 *
 * Built from the parts rather than sliced off `toISOString()`, which writes
 * years past 9999 as `+010000-01-01` — a string `utc` cannot read back, and the
 * reason stepping off the end of the calendar once looped for ever.
 */
function fromUtc(ms: number): IsoDate {
  const date = new Date(ms);
  const pad = (value: number, width: number) =>
    String(value).padStart(width, "0");
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}`;
}

/**
 * A real calendar date, spelled `YYYY-MM-DD`.
 *
 * `2026-02-30` matches the shape and is not a date; round-tripping through a
 * `Date` is what tells the two apart.
 */
export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  return fromUtc(utc(value)) === value;
}

/**
 * A real date in the years a chart may draw, `MIN_SERIES_YEAR` to
 * `MAX_SERIES_YEAR`. What the route and the SPA's link reader both ask of a
 * `from` and a `to`.
 */
export function isSeriesDate(value: unknown): value is IsoDate {
  if (!isIsoDate(value)) return false;
  const year = Number(value.slice(0, 4));
  return year >= MIN_SERIES_YEAR && year <= MAX_SERIES_YEAR;
}

/** `date` moved by `days` — negative for earlier. */
export function shiftDate(date: IsoDate, days: number): IsoDate {
  return fromUtc(utc(date) + days * DAY_MS);
}

/** Whole days from `from` to `to`; one for a single day's range is `0`. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((utc(to) - utc(from)) / DAY_MS);
}

/**
 * The calendar day an instant falls on in the store's zone.
 *
 * `2026-03-10T18:30:00Z` is the 11th in India, and an order placed then
 * belongs to the 11th's bucket — which is the whole reason the day is cut here
 * and never read off the UTC string.
 */
export function storeDate(
  instant: IsoDateTime | Date | number,
  timeZone: string = STORE_TIMEZONE,
): IsoDate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(instant));
  const part = (type: string) =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/**
 * The first day of the bucket `date` is in: itself, its Monday, or the 1st.
 *
 * Monday because that is what Postgres' `date_trunc('week', …)` returns — the
 * two sides have to put a Sunday order in the same week.
 */
export function bucketStart(date: IsoDate, bucket: OrderSeriesBucket): IsoDate {
  if (bucket === "day") return date;
  if (bucket === "month") return `${date.slice(0, 7)}-01`;
  const sinceMonday = (new Date(utc(date)).getUTCDay() + 6) % 7;
  return shiftDate(date, -sinceMonday);
}

export function nextBucket(start: IsoDate, bucket: OrderSeriesBucket): IsoDate {
  if (bucket === "day") return shiftDate(start, 1);
  if (bucket === "week") return shiftDate(start, 7);
  const year = Number(start.slice(0, 4));
  const month = Number(start.slice(5, 7));
  return month === 12
    ? `${String(year + 1)}-01-01`
    : `${String(year)}-${String(month + 1).padStart(2, "0")}-01`;
}

/**
 * Every bucket from the one holding `from` to the one holding `to`.
 *
 * Cannot run away. Dates are compared as dates, not as strings, and the walk
 * takes at most one step per day of the range plus one: no bucket is shorter
 * than a day, so no range holds more buckets than that, and a step that failed
 * to advance would stop the loop instead of filling memory with the same
 * string. Dates that are not dates throw; backwards is empty.
 */
export function bucketStarts(
  from: IsoDate,
  to: IsoDate,
  bucket: OrderSeriesBucket,
): IsoDate[] {
  if (!isIsoDate(from) || !isIsoDate(to)) {
    throw new RangeError(`Not a date range: ${String(from)}..${String(to)}`);
  }
  const last = utc(bucketStart(to, bucket));
  const first = bucketStart(from, bucket);
  const starts: IsoDate[] = [];
  const steps = Math.max(0, daysBetween(from, to)) + 2;
  for (
    let start = first, step = 0;
    step < steps && utc(start) <= last;
    start = nextBucket(start, bucket), step += 1
  ) {
    starts.push(start);
  }
  return starts;
}

/* ------------------------------------------------------------------- money */

/**
 * `"1234.5"` as paise, without ever passing through a float.
 *
 * Exported for the Sales report's sums (`sales.ts`), which add up money the
 * same exact way.
 */
export function toPaise(money: Money): bigint {
  const negative = money.startsWith("-");
  const [whole = "0", fraction = ""] = money.replace(/^-/, "").split(".");
  const paise =
    BigInt(whole || "0") * 100n + BigInt(fraction.padEnd(2, "0").slice(0, 2));
  return negative ? -paise : paise;
}

/** Paise back to money, always two decimals — `toPaise`'s other half. */
export function fromPaise(paise: bigint): Money {
  const negative = paise < 0n;
  const absolute = negative ? -paise : paise;
  const text = `${String(absolute / 100n)}.${String(absolute % 100n).padStart(2, "0")}`;
  return negative ? `-${text}` : text;
}

/* ---------------------------------------------------------------- assembly */

interface LineAccumulator {
  key: string | null;
  orders: number[];
  paise: bigint[];
  totalOrders: number;
  totalPaise: bigint;
}

function accumulator(key: string | null, length: number): LineAccumulator {
  return {
    key,
    orders: new Array<number>(length).fill(0),
    paise: new Array<bigint>(length).fill(0n),
    totalOrders: 0,
    totalPaise: 0n,
  };
}

function add(target: LineAccumulator, index: number, row: OrderSeriesRow) {
  const paise = toPaise(row.value);
  target.orders[index] = (target.orders[index] ?? 0) + row.orders;
  target.paise[index] = (target.paise[index] ?? 0n) + paise;
  target.totalOrders += row.orders;
  target.totalPaise += paise;
}

/**
 * The chart's data from grouped rows.
 *
 * Zero-fills the buckets, orders the lines, folds the tail into "Other" and
 * adds up the totals. Throws on a row whose bucket is not in the range's
 * bucket list: that would mean the two sides disagree about where a week
 * starts, and dropping the row quietly is how a chart ends up wrong.
 */
export function assembleOrderSeries(
  query: Pick<
    OrderSeriesQuery,
    "from" | "to" | "bucket" | "metric" | "splitBy"
  >,
  rows: readonly OrderSeriesRow[],
  timezone: string = STORE_TIMEZONE,
): OrderSeries {
  const buckets = bucketStarts(query.from, query.to, query.bucket);
  const position = new Map(buckets.map((start, index) => [start, index]));
  const split = query.splitBy !== undefined;

  const groups = new Map<string | null, LineAccumulator>();
  const everything = accumulator(null, buckets.length);

  for (const row of rows) {
    const index = position.get(row.bucket);
    if (index === undefined) {
      throw new Error(
        `Order series row for ${row.bucket} is outside ${query.from}..${query.to}`,
      );
    }
    add(everything, index, row);

    const key = split ? row.key : null;
    let group = groups.get(key);
    if (!group) {
      group = accumulator(key, buckets.length);
      groups.set(key, group);
    }
    add(group, index, row);
  }

  const byValue = query.metric === "value";
  const weight = (group: LineAccumulator): bigint =>
    byValue ? group.totalPaise : BigInt(group.totalOrders);

  let kept: LineAccumulator[];
  let other: LineAccumulator | null = null;

  if (!split) {
    kept = [groups.get(null) ?? accumulator(null, buckets.length)];
  } else {
    // Largest first; ties by key so the order never depends on how the rows
    // arrived. A line with no value sorts last among equals.
    const ranked = [...groups.values()].sort((a, b) => {
      const difference = weight(b) - weight(a);
      if (difference !== 0n) return difference > 0n ? 1 : -1;
      if (a.key === b.key) return 0;
      if (a.key === null) return 1;
      if (b.key === null) return -1;
      return a.key < b.key ? -1 : 1;
    });
    kept = ranked.slice(0, MAX_SERIES_LINES);
    const rest = ranked.slice(MAX_SERIES_LINES);
    if (rest.length > 0) {
      const folded = accumulator(null, buckets.length);
      for (const group of rest) {
        buckets.forEach((_, index) => {
          folded.orders[index] =
            (folded.orders[index] ?? 0) + (group.orders[index] ?? 0);
          folded.paise[index] =
            (folded.paise[index] ?? 0n) + (group.paise[index] ?? 0n);
        });
        folded.totalOrders += group.totalOrders;
        folded.totalPaise += group.totalPaise;
      }
      other = folded;
    }
  }

  const line = (group: LineAccumulator, isOther: boolean): OrderSeriesLine => ({
    key: isOther ? null : group.key,
    other: isOther,
    values: byValue
      ? group.paise.map(fromPaise)
      : group.orders.map((count) => String(count)),
    total: byValue ? fromPaise(group.totalPaise) : String(group.totalOrders),
  });

  return {
    from: query.from,
    to: query.to,
    bucket: query.bucket,
    metric: query.metric,
    splitBy: query.splitBy ?? null,
    timezone,
    buckets,
    lines: [
      ...kept.map((group) => line(group, false)),
      ...(other ? [line(other, true)] : []),
    ],
    totals: {
      orders: everything.totalOrders,
      value: fromPaise(everything.totalPaise),
    },
  };
}

/** One order, reduced to what the series counts. For the fixture adapter. */
export interface OrderSeriesPoint {
  placedAt: IsoDateTime;
  total: Money;
  /** The split field's value for this order, or null. */
  key: string | null;
}

/**
 * The grouped rows, from orders in memory — what Postgres' `group by` returns
 * on the API side.
 *
 * The caller has already applied the filter, the search, the range and the
 * cancelled switch; this only cuts each order's day in the store's zone and
 * groups.
 */
export function seriesRowsFromPoints(
  points: readonly OrderSeriesPoint[],
  bucket: OrderSeriesBucket,
  timezone: string = STORE_TIMEZONE,
): OrderSeriesRow[] {
  const groups = new Map<
    string,
    { bucket: IsoDate; key: string | null; orders: number; paise: bigint }
  >();

  for (const point of points) {
    const start = bucketStart(storeDate(point.placedAt, timezone), bucket);
    const id = `${start}|${point.key ?? "\u0000"}`;
    const group = groups.get(id) ?? {
      bucket: start,
      key: point.key,
      orders: 0,
      paise: 0n,
    };
    group.orders += 1;
    group.paise += toPaise(point.total);
    groups.set(id, group);
  }

  return [...groups.values()].map((group) => ({
    bucket: group.bucket,
    key: group.key,
    orders: group.orders,
    value: fromPaise(group.paise),
  }));
}

/* ----------------------------------------------------------------- payment */

/** How an order was paid for, as the chart and the filter know it. */
export type OrderPayment = "cod" | "prepaid";

/**
 * Cash on delivery or paid up front, from the order's normalised gateway.
 *
 * `cod` is the gateway the store's COD orders carry (`paymentGatewayKey` folds
 * `Cash on Delivery (COD)` and `cash_on_delivery` into it, #321). Any other
 * gateway — a card or UPI processor, the marketplace's own payment, a manual
 * payment a staff member recorded on a draft order — means the money was
 * collected before the parcel left, which is what prepaid means to the person
 * packing it. No gateway at all is neither: a draft order nobody has paid yet,
 * and calling it prepaid would be a guess.
 *
 * The API says the same in SQL (`payment` in `routes/orders.ts`); a test holds
 * the two to one answer.
 */
export function paymentKindOf(gatewayKey: string | null): OrderPayment | null {
  if (gatewayKey === null) return null;
  return gatewayKey === "cod" ? "cod" : "prepaid";
}
